import type { Card } from '@campfire/cards';

/** Where an ace may sit in a run. Never "around the corner" (K-A-2). */
export interface RunRules {
  /** A-2-3 allowed. */
  readonly aceLow: boolean;
  /** Q-K-A allowed. */
  readonly aceHigh: boolean;
}

/** Gin: ace is always low. */
export const ACE_LOW_RUNS: RunRules = { aceLow: true, aceHigh: false };
/** Rummy 500 and friends: A-2-3 or Q-K-A. */
export const ACE_EITHER_RUNS: RunRules = { aceLow: true, aceHigh: true };

export type MeldKind = 'set' | 'run';

const RANK_NUM: Readonly<Record<string, number>> = {
  A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13,
};

/** Ace = 1 … King = 13; jokers 0. */
export const rankNumber = (c: Card): number => RANK_NUM[c.rank] ?? 0;

/** Classic deadwood value: ace 1, pips face value, J/Q/K 10 (FR-MELD-04). */
export const pipValue = (c: Card): number => Math.min(10, rankNumber(c));

const distinctIds = (cards: readonly Card[]) => new Set(cards.map((c) => c.id)).size === cards.length;

/** Three or four cards of one rank, all different suits (FR-MELD-01). */
export function isSet(cards: readonly Card[]): boolean {
  if (cards.length < 3 || cards.length > 4) return false;
  const first = cards[0] as Card;
  if (first.rank === 'JK') return false;
  return cards.every((c) => c.rank === first.rank) && new Set(cards.map((c) => c.suit)).size === cards.length;
}

/**
 * The cards of a valid run in order low→high, or null (FR-MELD-01).
 * A run is three or more consecutive cards of one suit; the ace sits low or high per `rules`.
 */
export function runOrder(cards: readonly Card[], rules: RunRules): Card[] | null {
  if (cards.length < 3 || !distinctIds(cards)) return null;
  const suit = (cards[0] as Card).suit;
  if (suit === null || cards.some((c) => c.suit !== suit || c.rank === 'JK')) return null;
  const hasAce = cards.some((c) => c.rank === 'A');
  const tries: (1 | 14)[] = hasAce ? [...(rules.aceLow ? [1 as const] : []), ...(rules.aceHigh ? [14 as const] : [])] : [1];
  for (const ace of tries) {
    const pos = (c: Card) => (c.rank === 'A' ? ace : rankNumber(c));
    const sorted = [...cards].sort((a, b) => pos(a) - pos(b));
    if (sorted.every((c, i) => i === 0 || pos(c) === pos(sorted[i - 1] as Card) + 1)) return sorted;
  }
  return null;
}

export const isRun = (cards: readonly Card[], rules: RunRules): boolean => runOrder(cards, rules) !== null;

export function meldKind(cards: readonly Card[], rules: RunRules): MeldKind | null {
  if (isSet(cards)) return 'set';
  if (isRun(cards, rules)) return 'run';
  return null;
}

/** Cards of a meld in display order: runs low→high, sets by suit. Returns a copy for invalid input. */
export function orderMeld(cards: readonly Card[], rules: RunRules): Card[] {
  return runOrder(cards, rules) ?? [...cards].sort((a, b) => String(a.suit).localeCompare(String(b.suit)));
}

/** Whether `cards` can all be laid off together on an existing meld, keeping its kind (FR-MELD-02). */
export function canLayOff(cards: readonly Card[], meld: readonly Card[], rules: RunRules): boolean {
  const kind = meldKind(meld, rules);
  return cards.length > 0 && kind !== null && meldKind([...meld, ...cards], rules) === kind;
}

/** In an ordered run, true when its ace is played low (A-2-3…). */
export const aceIsLow = (orderedRun: readonly Card[]): boolean => orderedRun[0]?.rank === 'A';

function suitRows(cards: readonly Card[]): Map<string, Map<number, Card>> {
  const bySuit = new Map<string, Map<number, Card>>();
  for (const c of cards) {
    if (c.suit === null || c.rank === 'JK') continue;
    const row = bySuit.get(c.suit) ?? new Map<number, Card>();
    if (!row.has(rankNumber(c))) row.set(rankNumber(c), c);
    bySuit.set(c.suit, row);
  }
  return bySuit;
}

const at = (row: Map<number, Card>, p: number, rules: RunRules): Card | undefined =>
  p === 14 ? (rules.aceHigh ? row.get(1) : undefined) : p === 1 && !rules.aceLow ? undefined : row.get(p);

/** Every set (3- and 4-card) and run (each consecutive stretch of 3+) that can be made from `cards`. */
export function candidateMelds(cards: readonly Card[], rules: RunRules): Card[][] {
  const out: Card[][] = [];
  const byRank = new Map<string, Card[]>();
  for (const c of cards) {
    if (c.rank === 'JK') continue;
    const list = byRank.get(c.rank) ?? [];
    if (!list.some((x) => x.suit === c.suit)) list.push(c);
    byRank.set(c.rank, list);
  }
  for (const list of byRank.values()) {
    if (list.length < 3) continue;
    if (list.length === 4) out.push([...list]);
    for (let skip = 0; skip < list.length; skip++) {
      if (list.length === 3 && skip > 0) break;
      out.push(list.length === 3 ? [...list] : list.filter((_, i) => i !== skip));
    }
  }
  for (const row of suitRows(cards).values()) {
    for (let start = 1; start <= 12; start++) {
      const run: Card[] = [];
      for (let p = start; p <= 14; p++) {
        const c = at(row, p, rules);
        if (!c || run.includes(c)) break;
        run.push(c);
        if (run.length >= 3) out.push([...run]);
      }
    }
  }
  return out;
}

/**
 * Groups of `pool` cards that could be laid off together on `meld` (FR-MELD-02):
 * the missing suit of a 3-card set, or a contiguous extension at either or both ends of a run.
 */
export function layoffOptions(pool: readonly Card[], meld: readonly Card[], rules: RunRules): Card[][] {
  const kind = meldKind(meld, rules);
  if (kind === 'set') {
    if (meld.length >= 4) return [];
    const rank = (meld[0] as Card).rank;
    return pool.filter((c) => c.rank === rank && !meld.some((m) => m.suit === c.suit)).map((c) => [c]);
  }
  if (kind !== 'run') return [];
  const ordered = runOrder(meld, rules) as Card[];
  const row = suitRows(pool).get((ordered[0] as Card).suit as string) ?? new Map<number, Card>();
  const lo = aceIsLow(ordered) ? 1 : rankNumber(ordered[0] as Card);
  const hi = lo + ordered.length - 1;
  const below: Card[] = [];
  // A run can't hold the ace at both ends (matters with two decks).
  for (let p = lo - 1; p >= (hi === 14 ? 2 : 1); p--) {
    const c = at(row, p, rules);
    if (!c) break;
    below.push(c);
  }
  const above: Card[] = [];
  for (let p = hi + 1; p <= (lo === 1 ? 13 : 14); p++) {
    const c = at(row, p, rules);
    if (!c || below.includes(c)) break;
    above.push(c);
  }
  const out: Card[][] = [];
  for (let a = 0; a <= below.length; a++) {
    for (let b = 0; b <= above.length; b++) {
      if (a + b > 0) out.push([...below.slice(0, a), ...above.slice(0, b)]);
    }
  }
  return out;
}
